import { RuleEngineEntities } from '@bimser/components/dist/BCRuleManager';
import * as LayoutCreatorEntities from '@bimser/components/dist/BCLayoutCreator/common/entities';
import { FormItemTypes, IDictionary, isNullOrUndefined, MLHelper } from '@bimser/core';
import { Map } from 'immutable';
import dayjs from 'dayjs';
import { IControlProperties } from '../../../entities';
import { IDataSource, IVariableItem } from '../../../entities/common';
import { IControlDataGridColumn } from '../../../environments/runtime/components/DataGrid/entities';
import { formulaEvaluate } from '../rule';
import set from 'lodash/set';
import merge from 'lodash/merge';
import cloneDeep from 'lodash/cloneDeep';

export function convertActionValue(
	field: RuleEngineEntities.IField,
	value: any,
	data: Map<string, LayoutCreatorEntities.LayoutItem>,
	dataField: string[],
	isNotValueAssign?: boolean
) {
	const keys = field.propertyName;

	if (data) {
		return convertValue(field, value, data, dataField, keys, isNotValueAssign);
	}

	if (field.type === 'date' && (keys === 'value' || keys === 'text')) {
		return {
			value: value ? dayjs(value).startOf('day').valueOf() : null,
			dataField
		};
	} else if ((field.type === 'time' && keys === 'value') || keys === 'value') {
		const v = value ? dayjs(value).set('year', 1993).set('month', 10).set('date', 10) : value;
		return {
			value: v ? v.valueOf() : value,
			dataField
		};
	} else if (field.type === 'boolean' && isNullOrUndefined(value)) {
		value = false;
	}

	return { value, dataField };
}

function convertValue(
	field: RuleEngineEntities.IField,
	value: any,
	data: Map<string, LayoutCreatorEntities.LayoutItem>,
	dataField: string[],
	keys: string,
	isNotValueAssign?: boolean
) {
	const controlType: FormItemTypes.EControlTypes | FormItemTypes.EContainerTypes = data.getIn([
		field.name,
		'properties',
		'controlType'
	]);
	let convertedValue: { value: any; dataField: string[] } = null;

	switch (controlType) {
		case FormItemTypes.EContainerTypes.Form: {
			convertedValue = convertFormValue(field, data, dataField, keys);
			break;
		}
		case FormItemTypes.EControlTypes.ComboBox:
		case FormItemTypes.EControlTypes.Dropdown:
		case FormItemTypes.EControlTypes.RadioList:
		case FormItemTypes.EControlTypes.TreeView:
		case FormItemTypes.EControlTypes.AutoComplete: {
			convertedValue = convertAutoCompleteValue(field, value, data, keys);
			break;
		}
		case FormItemTypes.EControlTypes.ListBox:
		case FormItemTypes.EControlTypes.TagBox:
		case FormItemTypes.EControlTypes.Transfer: {
			convertedValue = convertTransferValue(
				field,
				value,
				data,
				dataField,
				keys,
				isNotValueAssign
			);
			break;
		}
		case FormItemTypes.EControlTypes.TextBox:
		case FormItemTypes.EControlTypes.ProtectedTextBox:
		case FormItemTypes.EControlTypes.TextArea: {
			convertedValue = convertTextAreaValue(
				field,
				value,
				data,
				dataField,
				keys,
				isNotValueAssign
			);
			break;
		}
		case FormItemTypes.EControlTypes.DateTimePicker: {
			convertedValue = convertedDateTimePickerValue(
				field,
				value,
				data,
				dataField,
				keys,
				isNotValueAssign
			);
			break;
		}
		case FormItemTypes.EControlTypes.HTMLTextBox:
		case FormItemTypes.EControlTypes.Password:
		case FormItemTypes.EControlTypes.SearchBox: {
			convertedValue = convertSearchBoxValue(field, value, data, dataField, keys);
			break;
		}
		case FormItemTypes.EControlTypes.DataGrid: {
			convertedValue = convertDataGridValue(field, value, data, dataField, keys);
			break;
		}
	}

	if (convertedValue) {
		return convertedValue;
	}

	return { value, dataField };
}

function convertDataGridValue(
	field: RuleEngineEntities.IField,
	value: any,
	data: Map<string, LayoutCreatorEntities.LayoutItem>,
	dataField: string[],
	keys: string
) {
	if (keys.includes('columns')) {
		let columns: Array<IControlDataGridColumn<IControlProperties>> = data.getIn([
			field.name,
			'properties',
			'columns'
		]);
		const dataGridPath = keys.split('.');

		columns = columns.map(column => {
			if (column.name === dataGridPath[1]) {
				column = set(column, dataGridPath.splice(2).toString().replace(',', '.'), value);
			}
			return column;
		});

		return {
			value: columns,
			dataField: ['columns']
		};
	}
}

function convertFormValue(
	field: RuleEngineEntities.IField,
	data: Map<string, LayoutCreatorEntities.LayoutItem>,
	dataField: string[],
	keys: string
) {
	if (keys.indexOf('variables.') === 0) {
		const variable: IVariableItem = data.getIn([field.name, 'properties', ...keys.split('.')]);

		if (variable) {
			const result = formulaEvaluate(variable.value, data);
			return {
				value: result,
				dataField
			};
		}
	}
}

function convertAutoCompleteValue(
	field: RuleEngineEntities.IField,
	value: any,
	data: Map<string, LayoutCreatorEntities.LayoutItem>,
	keys: string
) {
	if (keys === 'value' || keys === 'text' || keys === 'selectedItem.value') {
		const items: any[] = data.getIn([field.name, 'properties', 'items']);
		let newItems = cloneDeep(items);
		newItems = newItems.map(i =>
			merge(i, { selected: i.value.toString() === value.toString() })
		);

		return {
			value: newItems,
			dataField: ['items']
		};
	} else if (keys === 'selectedItem.text') {
		const items: any[] = data.getIn([field.name, 'properties', 'items']);
		const newItems = items.map(i => merge(i, { selected: i.text == value }));

		return {
			value: newItems,
			dataField: ['items']
		};
	} else if (keys.includes('selectedItem.field.')) {
		const rawData: any[] = data.getIn([field.name, 'properties', 'data']);
		const items: any[] = data.getIn([field.name, 'properties', 'items']);
		const dataSource: IDataSource = data.getIn([field.name, 'properties', 'dataSource']);
		const selectedItem = items.find(i => i.selected);

		if (selectedItem) {
			const row = rawData.find(i => i[dataSource.valueExpr] === selectedItem.value);
			const selectedField = keys.split('.')[0];

			return row[selectedField];
		}
	}
}

function convertTransferValue(
	field: RuleEngineEntities.IField,
	value: any,
	data: Map<string, LayoutCreatorEntities.LayoutItem>,
	dataField: string[],
	keys: string,
	isNotValueAssign?: boolean
) {
	if (keys === 'value' || keys === 'text') {
		const items: any[] = data.getIn([field.name, 'properties', 'items']);
		const valueSeparator: string = data.getIn(
			[field.name, 'properties', 'valueSeparator'],
			','
		);
		const values = value.split(valueSeparator);
		const newItems = items.map(i => {
			const selected = values.indexOf(i.key) > -1;
			if (i.selected != selected) {
				return Object.assign({}, i, { selected: selected });
			} else {
				return i;
			}
		});

		return {
			value: newItems,
			dataField: ['items']
		};
	}
}

function convertTextAreaValue(
	field: RuleEngineEntities.IField,
	value: any,
	data: Map<string, LayoutCreatorEntities.LayoutItem>,
	dataField: string[],
	keys: string,
	isNotValueAssign?: boolean
) {
	if (keys === 'value' || keys === 'text' || keys === 'multiLanguageText') {
		const maxLength: boolean = data.getIn([field.name, 'properties', 'maxLength']);
		const enableMultiLanguageText: boolean = data.getIn([
			field.name,
			'properties',
			'enableMultiLanguageText'
		]);
		const multiLanguageText: IDictionary<string> = data.getIn([
			field.name,
			'properties',
			'multiLanguageText'
		]);
		let newData: IDictionary<string> | string = Object.assign({}, multiLanguageText);
		const newValue = value?.substr(0, maxLength || value.length);

		if (enableMultiLanguageText) {
			newData[MLHelper.currentLanguage.culture] = newValue;
		} else {
			newData['system'] = newValue;
		}

		if (isNotValueAssign && enableMultiLanguageText) {
			newData = MLHelper.getMLText(newData);
		} else if (isNotValueAssign) {
			newData = newData['system'];
		}

		return {
			value: newData,
			dataField: ['multiLanguageText']
		};
	}
}

function convertSearchBoxValue(
	field: RuleEngineEntities.IField,
	value: any,
	data: Map<string, LayoutCreatorEntities.LayoutItem>,
	dataField: string[],
	keys: string
) {
	if (keys === 'value' || keys === 'text') {
		const maxLength: boolean = data.getIn([field.name, 'properties', 'maxLength']);
		const newValue = value?.substr(0, maxLength || value.length);

		return {
			value: newValue,
			dataField
		};
	}
}

function convertedDateTimePickerValue(
	field: RuleEngineEntities.IField,
	value: any,
	data: Map<string, LayoutCreatorEntities.LayoutItem>,
	dataField: string[],
	keys: string,
	isNotValueAssign?: boolean
) {
	if ((keys === 'value' || keys === 'text') && isNotValueAssign) {
		return {
			value: value ? dayjs(value).startOf('day').valueOf() : null,
			dataField
		};
	}
}
